import React from "react";
import { MessageCircle } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

/**
 * Normalize an Indonesian phone number string to wa.me format.
 * Accepts a single number or a string with multiple numbers (e.g. "0812.. atau 0813..").
 * Returns the FIRST valid number in wa.me format (62XXXXXXXXX), or null if none valid.
 */
function normalizeWa(raw) {
    if (!raw) return null;
    const s = String(raw);
    // Find candidate phone-number-like substrings: optional +, then 8+ digits possibly with spaces/dashes
    const candidates = s.match(/\+?[\d][\d\s\-]{7,}/g) || [];
    for (const c of candidates) {
        let d = c.replace(/[^\d+]/g, "");
        if (d.startsWith("+")) d = d.slice(1);
        if (d.startsWith("0")) d = "62" + d.slice(1);
        else if (!d.startsWith("62")) d = "62" + d;
        if (/^\d{9,15}$/.test(d)) return d;
    }
    return null;
}

/**
 * Floating WhatsApp button — fixed at bottom-right. Renders only if
 * a valid `whatsapp_number` is set in store settings.
 *
 * Props:
 *   message    — optional pre-fill message (URL-encoded internally).
 *   raised     — true to render higher (use when page has a sticky bottom bar).
 */
export default function WhatsAppFab({ message, raised = false }) {
    const { settings } = useSettings();
    const phone = normalizeWa(settings?.whatsapp_number);
    if (!phone) return null;

    const text = encodeURIComponent(message || `Halo ${settings?.name || "Toko"}, saya mau tanya produk yang ada di katalog.`);
    const href = `https://wa.me/${phone}?text=${text}`;
    // Default bottom: bottom-24 (96px) to clear the marquee bar (32-40px) + Emergent badge.
    // raised=true pushes it even higher for pages with sticky bottom bars.
    const bottomCls = raised ? "bottom-36 sm:bottom-32" : "bottom-24 sm:bottom-20";

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wa-fab"
            aria-label="Chat WhatsApp"
            title="Chat WhatsApp"
            className={`group fixed ${bottomCls} right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg shadow-[#25D366]/30 transition-all duration-200 hover:scale-105 hover:shadow-xl active:scale-95 sm:px-5 sm:py-3.5`}
        >
            <MessageCircle size={20} strokeWidth={2.1} className="shrink-0" fill="white" stroke="#25D366" />
            <span className="hidden sm:inline text-sm font-semibold tracking-tight">Chat WhatsApp</span>
        </a>
    );
}
